import PrivateJetDisplay from 'frontend/all-demands/private-jet-display.vue'
import { ClientCommunicationRules } from 'frontend/client-communication-rules'
import { ClientGroup } from 'frontend/client-groups'
import { Cluster } from 'frontend/clusters'
import CollapsibleItems from 'frontend/common/collapsible-items.vue'
import DurationWithOverride from 'frontend/common/duration-with-override.vue'
import Text from 'frontend/common/text.vue'
import TrueFalseLabel from 'frontend/common/true-false-label.vue'
import { DriverCategory } from 'frontend/driver-categories'
import { DriverSource } from 'frontend/driver-sources'
import { Driver } from 'frontend/drivers'
import { Location, LocationWithTp } from 'frontend/locations'
import { Osl } from 'frontend/osls'
import { KINDS as SA_KINDS } from 'frontend/shift-actions'
import ShiftJobAasmState from 'frontend/shift-jobs/shift-job-aasm-state.vue'
import ShiftJobComputedExtendedKind from 'frontend/shift-jobs/shift-job-computed-extended-kind.vue'
import ShiftJobCreatedBy from 'frontend/shift-jobs/shift-job-created-by.vue'
// eslint-disable-next-line import/default
import ShiftJobGroupingCause from 'frontend/shift-jobs/shift-job-grouping-cause.vue'
import { Shift } from 'frontend/shifts'
import { ShuttleService } from 'frontend/shuttle-services'
import { TransferGroupPassengersList } from 'frontend/transfer-groups'
import { transferGroupPassengersCount } from 'frontend/transfer-groups/helpers/transfer-group-passengers-count'
import {
  PROVIDED_STAMPS,
  TransportBooking,
  TransportBookingTimeRange,
} from 'frontend/transport-bookings'
import { TransportationPoint } from 'frontend/transportation-points'
import { VappType } from 'frontend/vapp-types'
import { VehicleCategory } from 'frontend/vehicle-categories'
import { VehicleModel } from 'frontend/vehicle-models'
import { VehicleSource } from 'frontend/vehicle-sources'
import { VehicleType } from 'frontend/vehicle-types'
import { Vehicle } from 'frontend/vehicles'
import { camelCase, padStart, trim, uniq } from 'lodash'
import { markRaw } from 'vue'

import { CUSTOM_JOB_KINDS } from './index'

const uniqComment = tg => {
  const tbs = (tg.transportBookings || []).filter(tb => tb.comment?.length)
  if (tbs?.length) {
    return uniq(tbs.map(el => trim(el.comment)))
  } else {
    return null
  }
}

const uniqNotes = tg => {
  const tbs = (tg.transportBookings || []).filter(tb => tb.notes?.length)
  if (tbs?.length) {
    return uniq(tbs.map(el => trim(el.notes)))
  } else {
    return null
  }
}

const uniqFlightNumber = tg => {
  const tbs = (tg.transportBookings || []).filter(tb => tb.flightNumber?.length)
  if (tbs?.length) {
    return uniq(tbs.map(el => trim(el.flightNumber)))
  } else {
    return null
  }
}

const uniqPrivateJets = tg => {
  if (!tg.transportBookings?.filter(tb => ['arrival', 'departure'].includes(tb.kind))?.length)
    return null
  if (
    tg.transportBookings.some(
      tb => ['arrival', 'departure'].includes(tb.kind) && tb.isFlightPrivate,
    ) &&
    tg.transportBookings.some(
      tb => ['arrival', 'departure'].includes(tb.kind) && !tb.isFlightPrivate,
    )
  ) {
    return tg.transportBookings.filter(tb => ['arrival', 'departure'].includes(tb.kind))
  } else {
    return [tg.transportBookings.filter(tb => ['arrival', 'departure'].includes(tb.kind))[0]]
  }
}

const uniqFlightClass = tg => {
  const tbs = (tg.transportBookings || []).filter(tb => tb.flightClass?.length)
  if (tbs?.length) {
    return uniq(tbs.map(el => trim(el.flightClass)))
  } else {
    return null
  }
}

const tgTbChanged = tg => {
  return tg.transportBookings?.some(tb => {
    if (!tb.originalAttributesChangedAt) return false
    if (!tb.originalChangesSeenAt) return true
    else if (dayjs(tb.originalChangesSeenAt).isAfter(tb.originalAttributesChangedAt)) return false
    else return true
  })
}

const tbsByOppositeRouteBookingId = tg =>
  (tg.transportBookings || []).filter(tb => tb.oppositeRouteBookingId?.length > 0)

const uniqTbProvidedStamps = tg => {
  const result = uniq(
    tg.transportBookings?.map(el => PROVIDED_STAMPS.find(s => s.value == el.providedStamp)?.label),
  )
  return result
}

const uniqTbRequestedTimes = item => {
  const result = {}
  item.transportBookings?.forEach(tb => {
    const key = dayjs(tb.requestedTime).format('HH:mm')
    result[key] = result[key] || tb.requestedTime
    // result[key].push(tb)
  })
  return Object.values(result)
}

const uniqTbRequestedDates = item => {
  const result = {}
  item.transportBookings?.forEach(tb => {
    const key = dayjs(tb.requestedTime).format('DD.MM.YYYY')
    result[key] = result[key] || tb.requestedTime
    // result[key].push(tb)
  })
  return Object.values(result)
}

const uniqTbsByTimeAndFixedTime = item => {
  const result = {}
  item.transportBookings?.forEach(tb => {
    const key = `${dayjs(tb.startAt).format('HH:mm')}${dayjs(tb.endAt).format('HH:mm')}${
      tb.providedStampFixed ? tb.providedStamp : ''
    }`
    result[key] = result[key] || []
    result[key].push(tb)
  })
  return Object.values(result)
}

const tbRemoteIds = tg => (tg.transportBookings || []).filter(tb => tb.remoteId?.length > 0)

export const FIELDS = [
  'id',
  'eid',
  'type',
  'aasm_state',
  'in_route_at',
  'at_pickup_site_at',
  'picked_up_at',
  'in_progress_at',
  'at_dropoff_site_at',
  'dropped_off_at',
  'no_show_at',
  'end_at',
  'start_at',
  'duration',
  'updated_at',
  'shift_action_kind',
  'bus_id',
  'source',
  'additional_clients',
  'description',
  'driver_comment',
  'all_client_groups',
  'subkind',
  'route_rules_applied',
  'sum_of_passengers_count',
  {
    operational_service_levels: [
      'id',
      'name',
      'font_color',
      'background_color',
      'border_color',
      'position',
      'description',
    ],
  },
  {
    vehicle_types: ['id', 'name'],
  },
  {
    shuttle_points: [
      'id',
      'position',
      'common_name',
      'arrival_at',
      'passengers_count',
      {
        location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }],
      },
    ],
  },
  {
    shuttle_service: ['id', 'eid', 'name', 'bus_id'],
  },
  {
    shift: [
      'id',
      'kind',
      'eid',
      {
        staging_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }],
      },
      { vapp_types: ['id', 'name', 'archived_at'] },
      {
        driver: [
          'id',
          'name',
          'surname',
          'assigned_phone',
          'personal_phone',
          'primary_phone',
          { cluster: ['id', 'name', 'time_zone'] },
          {
            driver_category: ['id', 'name', 'background_color', 'border_color', 'font_color'],
          },
          {
            driver_source: ['id', 'name'],
          },
        ],
      },
      {
        vehicle: [
          'id',
          'fleet_number',
          'registration_plate',
          { vapp_types: ['id', 'name', 'archived_at'] },
          { cluster: ['id', 'name', 'time_zone'] },
          { vehicle_model: ['id', 'name', { vehicle_brand: ['id', 'name'] }] },
          { vehicle_type: ['id', 'name'] },
          { vehicle_source: ['id', 'name', 'billable', 'maintainable'] },
          {
            vehicle_category: ['id', 'name', 'background_color', 'border_color', 'font_color'],
          },
        ],
      },
    ],
  },
  { end_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
  { start_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
  { end_transportation_point: ['id', 'name'] },
  { start_transportation_point: ['id', 'name'] },
  {
    transport_bookings: [
      'most_accurate_flight_time',
      'airport_iata_code',
      'eid',
      'pu',
      'id',
      'has_police_escort',
      'start_at',
      'start_location',
      'end_at',
      'comment',
      'notes',
      'tpt_comment',
      'flight_number',
      'flight_class',
      'is_flight_private',
      'terminal_number',
      'flight_at',
      'original_attributes_changed_at',
      'original_changes_seen_at',
      'kind',
      'extended_kind',
      'remote_id',
      'provided_stamp_fixed',
      'provided_stamp',
      'requested_time',
      'last_known_shift_id',
      'locked_osl_assignment',
      'opposite_route_booking_id',
      'opposite_route_booking_eid',
      'overridden_arrival_buffer',
      'overridden_departure_buffer',
      'overridden_driver_on_spot_buffer',
      'overridden_load_buffer',
      'overridden_safety_buffer',
      'calculated_arrival_buffer',
      'calculated_departure_buffer',
      'calculated_driver_on_spot_buffer',
      'calculated_load_buffer',
      'calculated_safety_buffer',
      'unassignment_seen',
      'updated_at',
      'created_at',
      'number_of_clients',
      'route_rules_applied',
      'grouping_cause',
      'flight_domestic_or_international',
      'asl_confirmed',
      'technical_state',
      'driver_on_spot_at',
      'travel_start_at',
      'safety_end_at',
      {
        transport_booking_grouping_rule: ['id', 'name', 'eid'],
      },
      {
        created_by: ['id', 'name', 'surname', 'email'],
      },
      { asl: ['id', 'name'] },
      {
        client: [
          'id',
          'name',
          'surname',
          'archived_at',
          'email',
          'phone',
          'event_phone',
          'personal_assistant_email',
          'client_function_remark',
          'remote_id',
          { tsl: ['id', 'name'] },
          { asl: ['id', 'name'] },
          { client_categories: ['id', 'name', 'group_path'] },
          { client_functions: ['id', 'name'] },
          {
            client_groups: [
              'id',
              'background_color',
              'border_color',
              'description',
              'font_color',
              'name',
            ],
          },
        ],
      },
      {
        client_group: [
          'id',
          'background_color',
          'border_color',
          'description',
          'font_color',
          'name',
        ],
      },
      { start_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
      { end_location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
      { start_transportation_point: ['id', 'name'] },
      { end_transportation_point: ['id', 'name'] },
      {
        batch_onsite_request: ['id', 'eid'],
      },
      {
        operational_service_level: [
          'id',
          'name',
          'font_color',
          'background_color',
          'border_color',
          'position',
          'description',
        ],
      },
      {
        client_communication_messages: [
          'id',
          'aasm_state',
          {
            client_communication_rule: [
              'id',
              'eid',
              'name',
              'kind',
              'exclusive',
              'resend_minutes_after_update',
              'resend_on_update',
              'send_minutes_before_start',
            ],
          },
        ],
      },
    ],
  },
  { vehicle_type: ['id', 'name'] },
  {
    operational_service_level: [
      'id',
      'name',
      'font_color',
      'background_color',
      'border_color',
      'position',
      'description',
    ],
  },
]

export const SUBSCRIPTIONS = {
  items: 'ShiftJobsMainChannel',
  locations: 'LocationsSimpleChannel',
  transportationPoints: 'TransportationPointsSimpleChannel',
  shifts: 'ShiftsSimpleChannel',
  clients: 'ClientsDetailedChannel',
  clusters: 'ClustersSimpleChannel',
  drivers: 'DriversDetailedChannel',
  driverCategories: 'DriverCategoriesSimpleChannel',
  vehicles: 'VehiclesDetailedChannel',
  vehicleTypes: 'VehicleTypesSimpleChannel',
  vehicleCategories: 'VehicleCategoriesSimpleChannel',
  vehicleModels: 'VehicleModelsSimpleChannel',
  vehicleBrands: 'VehicleBrandsSimpleChannel',
  transportBookings: 'TransportBookingsAllDemandsChannel',
  operationalServiceLevel: 'OperationalServiceLevelsDetailedChannel',
  clientCommunicationRules: 'ClientCommunicationRulesSimpleChannel',
}

export const DEFAULT_COLUMNS = [
  'eid',
  'tbEids',
  'tgTotalClients',
  'tgClients',
  'type',
  'aasmState',
  'pu',
  'do',
  'startAt',
  'endAt',
  'shift',
  'driver',
  'vehicle',
  'osl',
  'dayLabels',
  'updatedAt',
  'clientCommunicationRules',
]

export const COLUMNS_CONFIG = {
  eid: {
    label: 'EID',
    header: {
      sort: 'eid',
    },
    cell: {
      kind: 'simple',
      path: 'eid',
    },
  },
  tbEids: {
    label: 'TB EIDs',
    cell: {
      kind: 'component',
      component: markRaw(CollapsibleItems),
      componentProps: item => ({ items: item.transportBookings?.map(el => el.eid) }),
    },
  },
  type: {
    label: 'Type',
    header: {
      sort: 'computed_extended_kind',
    },
    cell: {
      kind: 'component',
      component: markRaw(ShiftJobComputedExtendedKind),
      componentProps: item => ({ item: item }),
    },
  },
  aasmState: {
    label: 'State',
    header: {
      sort: 'aasmStateForSort',
    },
    cell: {
      kind: 'component',
      component: markRaw(ShiftJobAasmState),
      componentProps: item => ({ item: item }),
    },
  },
  pu: {
    label: 'Pick-up/start',
    tooltip: 'Pickup location name',
    header: {
      sort: 'startLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(LocationWithTp),
      componentProps: sj => ({
        item: {
          id: sj.id,
          startTransportationPoint: sj.startTransportationPoint,
          startLocation:
            sj?.type == 'ShiftAction' && sj?.shiftActionKind != 'custom_job'
              ? null
              : sj?.startLocation,
        },
        kind: 'start',
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },
  startCluster: {
    label: 'Pick-up/start cluster',
    tooltip: 'Pickup cluster name',
    header: {
      sort: 'startLocationClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: item => ({
        cluster:
          item?.type == 'ShiftAction' && item?.shiftActionKind != 'custom_job'
            ? null
            : item?.startLocation?.cluster,
      }),
    },
  },
  startLocation: {
    label: 'Pick-up/start location',
    tooltip: 'Pickup location name',
    header: {
      sort: 'startLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: item => ({
        minWidth: 250,
        location:
          item?.type == 'ShiftAction' && item?.shiftActionKind != 'custom_job'
            ? null
            : item?.startLocation,
      }),
    },
  },
  startTransportationPoint: {
    label: 'Pick-up/start transportation point',
    tooltip: 'Pickup transportation point name',
    header: {
      sort: 'startTransportationPointName',
    },
    cell: {
      kind: 'component',
      component: markRaw(TransportationPoint),
      componentProps: item => ({ transportationPoint: item?.startTransportationPoint }),
    },
  },
  do: {
    label: 'Drop-off/end',
    tooltip: 'Dropoff location name',
    header: {
      sort: 'endLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(LocationWithTp),
      componentProps: sj => ({
        item: {
          id: sj.id,
          endTransportationPoint: sj.endTransportationPoint,
          endLocation:
            sj?.type == 'ShiftAction' && sj?.shiftActionKind != 'custom_job'
              ? null
              : sj?.endLocation,
        },
        kind: 'end',
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },
  endCluster: {
    label: 'Drop-off/end cluster',
    tooltip: 'Dropoff cluster name',
    header: {
      sort: 'endLocationClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: item => ({
        cluster:
          item?.type == 'ShiftAction' && item?.shiftActionKind != 'custom_job'
            ? null
            : item?.endLocation?.cluster,
      }),
    },
  },
  endLocation: {
    label: 'Drop-off/end location',
    tooltip: 'Dropoff location name',
    header: {
      sort: 'endLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: item => ({
        minWidth: 250,
        location:
          item?.type == 'ShiftAction' && item?.shiftActionKind != 'custom_job'
            ? null
            : item?.endLocation,
      }),
    },
  },
  endTransportationPoint: {
    label: 'Drop-off/end transportation point',
    tooltip: 'Dropoff transportation point name',
    header: {
      sort: 'endTransportationPointName',
    },
    cell: {
      kind: 'component',
      component: markRaw(TransportationPoint),
      componentProps: item => ({ transportationPoint: item?.endTransportationPoint }),
    },
  },
  startAt: {
    label: 'Pick-up/start datetime',
    tooltip: 'Pickup datetime',
    header: {
      sort: 'startAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item?.startAt, tz: item?.startLocation?.timeZone }),
    },
  },
  startTime: {
    label: 'Pick-up/start time',
    tooltip: 'Pickup time',
    header: {
      sort: 'startAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item?.startAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  startDate: {
    label: 'Pick-up/start date',
    tooltip: 'Pickup date',
    header: {
      sort: 'startAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item?.startAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  endAt: {
    label: 'Drop-off/end datetime',
    tooltip: 'Dropoff datetime',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item?.endAt, tz: item.endLocation?.timeZone }),
    },
  },
  endTime: {
    label: 'Drop-off/end time',
    tooltip: 'Dropoff time',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item?.endAt,
        format: 'timeOnly',
        tz: item.endLocation?.timeZone,
      }),
    },
  },
  endDate: {
    label: 'Drop-off/end date',
    tooltip: 'Dropoff date',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item?.endAt,
        format: 'dateOnly',
        tz: item.endLocation?.timeZone,
      }),
    },
  },
  duration: {
    label: 'Duration',
    header: {
      sort: 'duration',
    },
    cell: {
      kind: 'component',
      component: 'humanized-duration',
      componentProps: item => ({ minutes: item.duration }),
    },
  },

  flightDomesticOrInternational: {
    label: 'Flight kind',
    header: {
      sort: 'flightDomesticOrInternational',
    },
    cell: {
      kind: 'function',
      fn: item =>
        uniq(item.transportBookings?.map(el => el.flightDomesticOrInternational) || [])?.join(', '),
    },
  },

  resultingArrivalBuffer: {
    label: 'Arrival buffer',
    header: {
      sort: 'resultingArrivalBuffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(DurationWithOverride),
      componentProps: item => ({
        duration: item.calculatedArrivalBuffer,
        override: item.overriddenArrivalBuffer,
      }),
    },
  },

  resultingLoadBuffer: {
    label: 'Load buffer',
    header: {
      sort: 'resultingLoadBuffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(DurationWithOverride),
      componentProps: item => ({
        duration: item.calculatedLoadBuffer,
        override: item.overriddenLoadBuffer,
      }),
    },
  },

  resultingDriverOnSpotBuffer: {
    label: 'Driver on spot buffer',
    header: {
      sort: 'resultingDriverOnSpotBuffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(DurationWithOverride),
      componentProps: item => ({
        duration: item.calculatedDriverOnSpotBuffer,
        override: item.overriddenDriverOnSpotBuffer,
      }),
    },
  },

  resultingSafetyBuffer: {
    label: 'Safety buffer',
    header: {
      sort: 'resultingSafetyBuffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(DurationWithOverride),
      componentProps: item => ({
        duration: item.calculatedSafetyBuffer,
        override: item.overriddenSafetyBuffer,
      }),
    },
  },

  resultingDepartureBuffer: {
    label: 'Departure buffer',
    header: {
      sort: 'resultingDepartureBuffer',
    },
    cell: {
      kind: 'component',
      component: markRaw(DurationWithOverride),
      componentProps: item => ({
        duration: item.calculatedDepartureBuffer,
        override: item.overriddenDepartureBuffer,
      }),
    },
  },

  driverOnSpotAt: {
    label: 'Driver on spot at',
    header: {
      sort: 'driverOnSpotAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.driverOnSpotAt }),
    },
  },

  travelStartAt: {
    label: 'Travel start at',
    header: {
      sort: 'travelStartAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.travelStartAt }),
    },
  },

  safetyEndAt: {
    label: 'Safety end at',
    header: {
      sort: 'safetyEndAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.safetyEndAt }),
    },
  },

  currentEta: {
    label: 'Driver App ETA',
    header: {
      sort: 'driverAppCurrentEta',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: tg => ({
        t: tg.driverAppCurrentEta,
        attr: 'driverAppCurrentEta',
        tz: tg.startLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  dayLabels: {
    label: 'Occasion',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: tg.dayLabels,
        limit: 5,
        component: 'erebus-label',
        componentProps: c => ({ item: c }),
      }),
    },
  },

  clientGroups: {
    label: 'Client Groups',
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: el => ({
        items: el?.allClientGroups,
        limit: 5,
        component: markRaw(ClientGroup),
        componentProps: c => ({ clientGroup: c }),
      }),
    },
  },

  vappTypes: {
    label: 'Required VAPP Types',
    header: {
      sort: 'shiftVappTypesNameForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.shift?.vappTypes,
        limit: 5,
        allowWrap: true,
        withWrapper: true,
        wrapperClass: 'd-flex flex-wrap align-items-center justify-content-start',
        mainClass: 'd-inline-flex flex-wrap align-items-center justify-content-start',
        component: markRaw(VappType),
        componentProps: vt => ({ vappType: vt }),
      }),
    },
  },

  availableVappTypes: {
    label: 'Available VAPP Types',
    header: {
      sort: 'shiftVehicleVappTypesNameForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.shift?.vehicle?.vappTypes,
        limit: 5,
        allowWrap: true,
        withWrapper: true,
        wrapperClass: 'd-flex flex-wrap align-items-center justify-content-start',
        mainClass: 'd-inline-flex flex-wrap align-items-center justify-content-start',
        component: markRaw(VappType),
        componentProps: vt => ({ vappType: vt }),
      }),
    },
  },

  shift: {
    label: 'Assigned Shift',
    header: {
      sort: 'shiftStagingLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Shift),
      componentProps: item => ({ shift: item?.shift, detailed: true }),
    },
  },

  shiftDetails: {
    label: 'Assigned Shift Details',
    tooltip: 'Shift details (driver, vehicle etc.)',
    header: {
      sort: 'shiftStagingLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Shift),
      componentProps: item => ({
        shift: item?.shift,
        detailed: true,
        withDriver: true,
        withVehicle: true,
      }),
    },
  },
  driver: {
    label: 'Driver',
    header: {
      sort: 'shiftDriverReverseFullName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Driver),
      componentProps: item => ({ driver: item?.shift?.driver, detailed: true }),
    },
  },
  driverName: {
    label: 'Driver name',
    header: {
      sort: 'shiftDriverName',
    },
    cell: {
      kind: 'simple',
      path: 'shift.driver.name',
    },
  },
  driverSurname: {
    label: 'Driver surname',
    header: {
      sort: 'shiftDriverSurname',
    },
    cell: {
      kind: 'simple',
      path: 'shift.driver.surname',
    },
  },
  driverPhoneNumber: {
    label: 'Driver phone number',
    header: {
      sort: 'shiftDriverMainPhone',
    },
    cell: {
      kind: 'component',
      component: 'phone-number',
      componentProps: sj => ({
        phoneNumber: sj.shift?.driver?.[camelCase(sj.shift?.driver?.primaryPhone || '')],
      }),
    },
  },
  driverCategory: {
    label: 'Driver category',
    header: {
      sort: 'shiftDriverCategoryName',
    },
    cell: {
      kind: 'component',
      component: markRaw(DriverCategory),
      componentProps: sj => ({
        driverCategory: sj.shift?.driver?.driverCategory,
      }),
    },
  },

  driverSource: {
    label: 'Driver source',
    header: {
      sort: 'shiftDriverDriverSourceName',
    },
    cell: {
      kind: 'component',
      component: markRaw(DriverSource),
      componentProps: tg => ({
        driverSource: tg.shift?.driver?.driverSource,
      }),
    },
  },
  driverComment: {
    label: 'Driver comment',
    header: {
      sort: 'driver_comment',
    },
    cell: {
      kind: 'component',
      component: 'ellipsis-with-tooltip',
      componentProps: item => ({
        text: item.driverComment,
        limit: 250,
      }),
    },
  },
  vehicle: {
    label: 'Vehicle',
    header: {
      // sort: 'shiftVehicleReverseFullName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Vehicle),
      componentProps: item => ({
        vehicle: item?.shift?.vehicle,
        detailed: true,
        showCluster: false,
      }),
    },
  },
  vehicleFleetNumber: {
    label: 'Vehicle fleet number',
    header: {
      sort: ['shiftVehicleFleetNumberPrefix', 'shiftVehicleFleetNumberInt'],
    },
    cell: {
      kind: 'simple',
      path: 'shift.vehicle.fleetNumber',
    },
  },
  vehicleRegistrationPlate: {
    label: 'Vehicle reg. plate',
    header: {
      sort: 'shiftVehicleRegistrationPlate',
    },
    cell: {
      kind: 'simple',
      path: 'shift.vehicle.registrationPlate',
    },
  },
  vehicleCluster: {
    label: 'Vehicle cluster',
    header: {
      sort: 'shiftVehicleVehicleClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: sj => ({
        cluster: sj.shift?.vehicle?.cluster,
      }),
    },
  },
  vehicleCategory: {
    label: 'Vehicle category',
    header: {
      sort: 'shiftVehicleVehicleCategoryName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleCategory),
      componentProps: sj => ({
        vehicleCategory: sj.shift?.vehicle?.vehicleCategory,
      }),
    },
  },

  vehicleSource: {
    label: 'Vehicle source',
    header: {
      sort: 'shiftVehicleVehicleSourceName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleSource),
      componentProps: tg => ({
        vehicleSource: tg.shift?.vehicle?.vehicleSource,
      }),
    },
  },

  vehicleType: {
    label: 'Vehicle type',
    header: {
      sort: 'shiftVehicleVehicleTypeName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleType),
      componentProps: sj => ({
        vehicleType: sj.shift?.vehicle?.vehicleType,
      }),
    },
  },
  vehicleModel: {
    label: 'Vehicle model',
    header: {
      sort: 'shiftVehicleVehicleModelName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleModel),
      componentProps: sj => ({
        vehicleModel: sj.shift?.vehicle?.vehicleModel,
      }),
    },
  },
  osl: {
    label: 'Operational Service Level',
    header: {
      sort: 'operationalServiceLevelPosition',
    },
    cell: {
      kind: 'component',
      component: markRaw(Osl),
      componentProps: item => ({
        osl: item?.operationalServiceLevel,
        hide: !!(item?.type == 'ShiftAction'),
      }),
    },
  },
  tgGroupingCause: {
    label: 'Grouping cause',
    cell: {
      kind: 'component',
      component: markRaw(ShiftJobGroupingCause),
      componentProps: item => ({ item: item }),
    },
  },
  hasPoliceEscort: {
    label: 'Has police escort',
    header: {
      sort: 'hasPoliceEscort',
    },
    cell: {
      kind: 'component',
      component: TrueFalseLabel,
      componentProps: item => ({ item: !!item.transportBookings?.find(el => el.hasPoliceEscort) }),
    },
  },
  shuttleService: {
    label: 'Shuttle Service',
    header: {
      sort: 'shuttleServiceLowerName',
    },
    cell: {
      kind: 'component',
      component: markRaw(ShuttleService),
      componentProps: el => ({
        shuttleService: el.shuttleService,
      }),
    },
  },
  sumOfPassengersCount: {
    label: 'ST Pax',
    tooltip: 'Shuttle transfer: sum of passengers count',
    header: {
      sort: 'sum_of_passengers_count',
    },
    cell: {
      class: 'text-center',
      kind: 'simple',
      path: 'sumOfPassengersCount',
    },
  },
  passengersCountPerPoint: {
    label: 'ST Pax per point',
    tooltip: 'Shuttle transfer: passengers count per point',
    header: {
      sort: 'sum_of_passengers_count',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: st => ({
        items: (st.shuttlePoints || [])
          .sort((a, b) => a.position - b.position)
          .map(
            (el, i) =>
              `${padStart(el.passengersCount.toString(), 2, '0')} at #${i + 1} ${
                el.location?.name
              }`,
          )
          .filter(x => !!x),
        limit: 3,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  createdBys: {
    label: 'Created By',
    cell: {
      kind: 'component',
      component: markRaw(ShiftJobCreatedBy),
      componentProps: sj => ({
        item: sj,
      }),
    },
  },

  createdDatetime: {
    label: 'Created datetime',
    header: {
      sort: 'createdAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: tg => ({ t: tg.createdAt }),
    },
  },

  createdTime: {
    label: 'Created Time',
    header: {
      sort: 'createdAtTime',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: tg => ({
        t: tg.createdAt,
        format: 'timeOnly',
      }),
    },
  },

  createdDate: {
    label: 'Created Date',
    header: {
      sort: 'createdAtDate',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: tg => ({
        t: tg.createdAt,
        format: 'dateOnly',
      }),
    },
  },

  updatedAt: {
    label: 'Updated At',
    header: {},
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item?.updatedAt }),
    },
  },
  updatedTime: {
    label: 'Updated Time',
    header: {},
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item?.updatedAt, format: 'timeOnly' }),
    },
  },
  updatedDate: {
    label: 'Updated Date',
    header: {},
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item?.updatedAt, format: 'dateOnly' }),
    },
  },
  saKind: {
    label: 'Shift Action kind',
    header: {
      sort: 'shiftActionKind',
    },
    cell: {
      kind: 'function',
      fn: item => SA_KINDS[item.shiftActionKind],
    },
  },
  saSubkind: {
    label: 'Shift Action - job type',
    header: {
      sort: 'subkind',
    },
    cell: {
      kind: 'function',
      fn: item => CUSTOM_JOB_KINDS.find(k => k.value == item.subkind)?.label,
    },
  },
  backedUpLocation: {
    label: 'Shift Action - backed up location',
    header: {
      sort: 'backupLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: item => ({
        minWidth: 250,
        location:
          item?.type == 'ShiftAction' && item.shiftActionKind == 'location_backup'
            ? item?.startLocation
            : null,
      }),
    },
  },

  tgClients: {
    label: 'Clients',
    tooltip: 'Clients of given transfer group',
    header: {
      sort: 'clientAndClientGroupFullNames',
    },
    cell: {
      kind: 'component',
      component: markRaw(TransferGroupPassengersList),
      componentProps: item => ({
        transferGroup: item,
      }),
    },
  },
  tgClientNames: {
    label: 'Client names',
    tooltip: 'Client names of given transfer group',
    header: {
      sort: 'clientNames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.name)).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  tgClientSurnames: {
    label: 'Client surnames',
    tooltip: 'Client surnames of given transfer group',
    header: {
      sort: 'clientSurnames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.surname)).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  tgClientTsl: {
    label: 'Client TSLs',
    tooltip: 'Client TSLs of given transfer group',
    header: {
      sort: 'clientTslNames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.client?.tsl?.name)).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },

  tgClientAsls: {
    label: 'Client ASLs',
    tooltip: 'Client ASLs of given transfer group',
    header: {
      sort: 'clientAslNames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.client?.asl?.name)).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },

  tgTbAsls: {
    label: 'TB ASLs',
    tooltip: 'TB ASLs of given transfer group',
    header: {
      sort: 'tbAslNames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.asl?.name)).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },

  tgAslConfirmed: {
    label: 'ASL Confirmed',
    header: {
      sort: 'tbAslConfirmed',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.aslConfirmed)),
        limit: 5,
        component: markRaw(TrueFalseLabel),
        componentProps: item => ({ item: item }),
      }),
    },
  },

  tgClientFunctions: {
    label: 'Client Functions',
    tooltip: 'Client Functions of given transfer group',
    header: {
      sort: 'clientFunctionNames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(
          tg.transportBookings.map(el => el.client?.clientFunctions?.map(el => el.name)).flat(),
        ).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  tgClientCategories: {
    label: 'Client Group Paths',
    tooltip: 'Client Group Paths of given transfer group',
    header: {
      sort: 'clientCategoryNames',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(
          tg.transportBookings
            .map(el => el.client?.clientCategories?.map(el => el.groupPath).flat())
            .flat(),
        ).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  tgClientFunctionRemarks: {
    label: 'Client Function Remarks',
    tooltip: 'Client Function Remarks of given transfer group',
    header: {
      sort: 'clientFunctionRemarks',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.clientFunctionRemark)).filter(
          x => !!x,
        ),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  tgClientEmails: {
    label: 'Client Emails',
    tooltip: 'Client Emails of given transfer group',
    header: {
      sort: 'clientEmails',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.email)).filter(x => !!x),
        limit: 5,
        component: 'email',
        componentProps: item => ({ email: item }),
      }),
    },
  },
  tgClientPersonalAssistantEmails: {
    label: 'Client PA Emails',
    tooltip: 'Client Personal Assistant Emails of given transfer group',
    header: {
      sort: 'clientPersonalAssistantEmails',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.personalAssistantEmail)).filter(
          x => !!x,
        ),
        limit: 5,
        component: 'email',
        componentProps: item => ({ email: item }),
      }),
    },
  },
  tgClientPhones: {
    label: 'Client Phones',
    tooltip: 'Client Phones of given transfer group',
    header: {
      sort: 'clientPhones',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.phone)).filter(x => !!x),
        limit: 5,
        component: 'phone-number',
        componentProps: item => ({ phoneNumber: item }),
      }),
    },
  },
  tgClientEventPhones: {
    label: 'Client Event Phones',
    tooltip: 'Client Event Phones of given transfer group',
    header: {
      sort: 'clientEventPhones',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.eventPhone)).filter(x => !!x),
        limit: 5,
        component: 'phone-number',
        componentProps: item => ({ phoneNumber: item }),
      }),
    },
  },
  tgClientRemoteIds: {
    label: 'Client Remote IDs',
    tooltip: 'Client Remote IDs of given transfer group',
    header: {
      sort: 'clientRemoteIds',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings.map(el => el.client?.remoteId)).filter(x => !!x),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },
  tgTbChanged: {
    label: 'TB changed',
    cell: {
      kind: 'component',
      component: 'true-false-label',
      componentProps: tg => ({
        item: tgTbChanged(tg),
        revertColors: true,
        hide: !tgTbChanged(tg),
      }),
    },
  },
  tgTotalClients: {
    label: 'TG Pax',
    tooltip: 'Total number of clients in transfer group',
    header: {
      sort: 'clientsCount',
    },
    cell: {
      class: 'text-align-center',
      kind: 'function',
      fn: item => transferGroupPassengersCount(item)?.toString(),
    },
  },

  clientCommunicationRules: {
    label: 'CC Rules',
    header: {
      sort: 'clientCommunicationRulesForSort',
    },
    cell: {
      kind: 'component',
      component: markRaw(ClientCommunicationRules),
      componentProps(item) {
        const allRules = item.transportBookings
          .map(transportBooking => {
            return transportBooking.clientCommunicationMessages
              .filter(message => !['draft', 'discarded'].includes(message.aasmState))
              .map(message => {
                return message.clientCommunicationRule
              })
          })
          .flat()
        const uniqueRules = allRules.filter((rule, index) => allRules.indexOf(rule) === index)
        return {
          transportBookingEids: uniqueRules.length
            ? item.transportBookings.map(tb => tb.eid)
            : null,
          clientCommunicationRules: uniqueRules,
        }
      },
    },
  },

  tbTimes: {
    label: 'TB Times',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqTbsByTimeAndFixedTime(tg),
        limit: 5,
        component: markRaw(TransportBookingTimeRange),
        componentProps: itemGroup => ({
          transportBooking: itemGroup[0],
          attrStart: 'startAt',
          attrEnd: 'endAt',
          showAnchor: true,
          format: 'timeOnly',
          counter: itemGroup.length,
        }),
      }),
    },
  },

  tbOppositeRouteBooking: {
    label: 'TBs Opposite Route Bookings',
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: tbsByOppositeRouteBookingId(tg),
        limit: 5,
        component: markRaw(TransportBooking),
        componentProps: item => ({
          transportBooking: item.oppositeRouteBookingId
            ? { id: item.oppositeRouteBookingId, eid: item.oppositeRouteBookingEid }
            : null,
          eidOnly: true,
        }),
      }),
    },
  },

  tbComment: {
    label: 'TB Comment',
    header: {
      sort: 'tbComment',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqComment(tg),
        limit: 5,
        allowWrap: true,
        component: 'ellipsis-with-tooltip',
        componentProps: item => ({
          text: item,
          limit: 250,
        }),
      }),
    },
  },

  tbNotes: {
    label: 'TB Request Notes',
    header: {
      sort: 'notes',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqNotes(tg),
        limit: 5,
        allowWrap: true,
        component: 'ellipsis-with-tooltip',
        componentProps: item => ({
          text: item,
          limit: 250,
        }),
      }),
    },
  },
  tbFlightNumber: {
    label: 'Travel No',
    header: {
      sort: 'flightNumber',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqFlightNumber(tg),
        limit: 5,
        allowWrap: true,
        component: 'ellipsis-with-tooltip',
        componentProps: item => ({
          text: item,
          limit: 250,
        }),
      }),
    },
  },

  tbPrivateJet: {
    label: 'Private Jet',
    header: {
      sort: 'isFlightPrivateForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => {
        const tbs = uniqPrivateJets(tg)
        return {
          items: tbs,
          limit: 4,
          component: PrivateJetDisplay,
          componentProps: item => ({
            transportBooking: item,
            isSole: tbs.length == 1,
          }),
        }
      },
    },
  },

  tbFlightClass: {
    label: 'Flight Class',
    header: {
      sort: 'flightClass',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqFlightClass(tg),
        limit: 5,
        allowWrap: true,
        component: 'ellipsis-with-tooltip',
        componentProps: item => ({
          text: item,
          limit: 200,
        }),
      }),
    },
  },

  tbFlightAtDatetime: {
    label: 'Travel arrival/departure datetime',
    header: {
      sort: 'flightAt',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.flightAt)).filter(x => !!x),
        limit: 5,
        allowWrap: true,
        component: 'formatted-time',
        componentProps: item => ({
          t: item,
          tz: tg.startLocation?.timeZone,
        }),
      }),
    },
  },

  tbFlightAtTime: {
    label: 'Travel arrival/departure time',
    header: {
      sort: 'flightAt',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.flightAt)).filter(x => !!x),
        limit: 5,
        allowWrap: true,
        component: 'formatted-time',
        componentProps: item => ({
          t: item,
          tz: tg.startLocation?.timeZone,
          format: 'timeOnly',
        }),
      }),
    },
  },

  tbFlightAtDate: {
    label: 'Travel arrival/departure date',
    header: {
      sort: 'flightAt',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.flightAt)).filter(x => !!x),
        limit: 5,
        allowWrap: true,
        component: 'formatted-time',
        componentProps: item => ({
          t: item,
          tz: tg.startLocation?.timeZone,
          format: 'dateOnly',
        }),
      }),
    },
  },

  tbTerminalNumber: {
    label: 'Terminal No.',
    header: {
      sort: 'terminalNumber',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniq(tg.transportBookings?.map(el => el.terminalNumber)).filter(x => !!x),
        limit: 5,
        allowWrap: true,
        component: 'ellipsis-with-tooltip',
        componentProps: item => ({
          text: item,
          limit: 200,
        }),
      }),
    },
  },

  tbAirportIataCode: {
    label: 'TB Airport IATA code',
    header: {
      sort: 'airportIataCode',
    },
    cell: {
      kind: 'function',
      fn: item => item.airportIataCodes?.join(', '),
    },
  },

  routeRulesApplied: {
    label: 'FIFA GIS rules applied',
    header: {
      sort: 'joinedRouteRulesApplied',
    },
    cell: {
      // class: 'text-nowrap',
      kind: 'function',
      fn: tg => (tg.routeRulesApplied || []).map(r => r.name).join('; '),
    },
  },

  providedStamp: {
    label: 'TB Requested Time Kinds',
    header: {
      sort: 'providedStamp',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqTbProvidedStamps(tg),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item }),
      }),
    },
  },

  tbRequestedDateTimes: {
    label: 'TB Requested Datetimes',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqTbRequestedTimes(tg),
        limit: 5,
        component: 'formatted-time',
        componentProps: item => ({
          t: item,
          tz: tg.startLocation?.timeZone,
        }),
      }),
    },
  },

  tbRequestedTimes: {
    label: 'TB Requested Times',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqTbRequestedTimes(tg),
        limit: 5,
        component: 'formatted-time',
        componentProps: item => ({
          t: item,
          tz: tg.startLocation?.timeZone,
          format: 'timeOnly',
        }),
      }),
    },
  },

  tbRequestedDates: {
    label: 'TB Requested Dates',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: uniqTbRequestedDates(tg),
        limit: 5,
        component: 'formatted-time',
        componentProps: item => ({
          t: item,
          tz: tg.startLocation?.timeZone,
          format: 'dateOnly',
        }),
      }),
    },
  },

  tbRemoteIds: {
    label: 'TB Remote IDs',
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: tg => ({
        items: tbRemoteIds(tg),
        limit: 5,
        component: markRaw(Text),
        componentProps: item => ({ text: item.remoteId }),
      }),
    },
  },

  inRouteDatetime: {
    label: 'In route datetime',
    tooltip: 'Datetime when status became "in route"',
    header: {
      sort: 'inRouteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.inRouteAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  inRouteDate: {
    label: 'In route date',
    tooltip: 'Date when status became "in route"',
    header: {
      sort: 'inRouteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.inRouteAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  inRouteTime: {
    label: 'In route time',
    tooltip: 'Time when status became "in route"',
    header: {
      sort: 'inRouteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.inRouteAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  atPickupSiteDatetime: {
    label: 'At pick-up site datetime',
    tooltip: 'Datetime when status became "at pickup site"',
    header: {
      sort: 'atPickupSiteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.atPickupSiteAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  atPickupSiteDate: {
    label: 'At pick-up site date',
    tooltip: 'Date when status became "at pickup site"',
    header: {
      sort: 'atPickupSiteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.atPickupSiteAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  atPickupSiteTime: {
    label: 'At pick-up site time',
    tooltip: 'Time when status became "at pickup site"',
    header: {
      sort: 'atPickupSiteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.atPickupSiteAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  pickedUpDatetime: {
    label: 'Picked up datetime',
    tooltip: 'Datetime when status became "picked up"',
    header: {
      sort: 'pickedUpAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.pickedUpAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  pickedUpDate: {
    label: 'Picked up date',
    tooltip: 'Date when status became "picked up"',
    header: {
      sort: 'pickedUpAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.pickedUpAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  pickedUpTime: {
    label: 'Picked up time',
    tooltip: 'Time when status became "picked up"',
    header: {
      sort: 'pickedUpAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.pickedUpAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  inProgressDatetime: {
    label: 'In progress datetime',
    tooltip: 'Datetime when status became "in progress"',
    header: {
      sort: 'inProgressAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.inProgressAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  inProgressDate: {
    label: 'In progress date',
    tooltip: 'Date when status became "in progress"',
    header: {
      sort: 'inProgressAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.inProgressAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  inProgressTime: {
    label: 'In progress time',
    tooltip: 'Time when status became "in progress"',
    header: {
      sort: 'inProgressAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.inProgressAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  atDropoffSiteDatetime: {
    label: 'At drop-off site datetime',
    tooltip: 'Datetime when status became "at dropoff site"',
    header: {
      sort: 'atDropoffSiteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.atDropoffSiteAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  atDropoffSiteDate: {
    label: 'At drop-off site date',
    tooltip: 'Date when status became "at dropoff site"',
    header: {
      sort: 'atDropoffSiteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.atDropoffSiteAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  atDropoffSiteTime: {
    label: 'At drop-off site time',
    tooltip: 'Time when status became "at dropoff site"',
    header: {
      sort: 'atDropoffSiteAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.atDropoffSiteAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  droppedOffDatetime: {
    label: 'Dropped off datetime',
    tooltip: 'Datetime when status became "dropped of"',
    header: {
      sort: 'droppedOffAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.droppedOffAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  droppedOffDate: {
    label: 'Dropped off date',
    tooltip: 'Date when status became "dropped of"',
    header: {
      sort: 'droppedOffAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.droppedOffAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  droppedOffTime: {
    label: 'Dropped off time',
    tooltip: 'Time when status became "dropped of"',
    header: {
      sort: 'droppedOffAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.droppedOffAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  noShowDatetime: {
    label: 'Incomplete datetime',
    tooltip: 'Datetime when status became "incomplete"',
    header: {
      sort: 'noShowAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.noShowAt,
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  noShowDate: {
    label: 'Incomplete date',
    tooltip: 'Date when status became "incomplete"',
    header: {
      sort: 'noShowAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.noShowAt,
        format: 'dateOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
  noShowTime: {
    label: 'Incomplete time',
    tooltip: 'Time when status became "incomplete"',
    header: {
      sort: 'noShowAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.noShowAt,
        format: 'timeOnly',
        tz: item.startLocation?.timeZone,
      }),
    },
  },
}
